import { Button, formatHireDate, SpinnerWithBackground, Text, VsOverall } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { DISTRIBUTION_TIERS, formatDealsInvolved, getRatingColor } from './components/shared'
import { useRatingScaleMax } from './components/useRatingScaleMax'
import { useDealTeamAccess } from './hooks/useDealTeamAccess'
import type { AggregationStore } from './stores/aggregationStore'
import { useAggregationStore } from './stores/aggregationStore'

type DealReport = NonNullable<AggregationStore['participantDealReport']>
type ReportQuestion = DealReport['questions'][number]
type ReportReviewer = ReportQuestion['reviewers'][number]
type ReportSource = ReportReviewer['source']
type ParticipantProfile = NonNullable<AggregationStore['participantProfile']>

const NA_LEGEND_COLOR = '#E5E7EB'
const NA_LEGEND_TEXT = '#6B7280'

const SOURCE_LABELS: Record<ReportSource, string> = {
	self: 'Self',
	manager: 'Manager',
	peers: 'Peers',
	directReports: 'Direct Reports',
	external: 'External',
	others: 'Others',
}

const SOURCE_ORDER: ReportSource[] = ['self', 'manager', 'peers', 'directReports', 'external', 'others']

function RatingChip({ value }: { value: number | null }) {
	const scaleMax = useRatingScaleMax()
	if (value === null) return <span className='text-sm text-neutral-400'>—</span>
	return (
		<span
			className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold ${getRatingColor(value, scaleMax)}`}>
			{value.toFixed(2)}
		</span>
	)
}

function ColorLegend() {
	return (
		<div className='flex flex-wrap gap-x-6 gap-y-2'>
			{DISTRIBUTION_TIERS.map((tier) => (
				<div key={tier.key} className='flex items-center gap-2'>
					<div className='h-4 w-4 rounded' style={{ backgroundColor: tier.color }} />
					<span className='text-sm text-neutral-700'>{tier.label}</span>
				</div>
			))}
			<div className='flex items-center gap-2'>
				<div className='h-4 w-4 rounded' style={{ backgroundColor: NA_LEGEND_COLOR }} />
				<span className='text-sm text-neutral-700'>Not Applicable</span>
			</div>
		</div>
	)
}

function DistributionSegment({ count, color, total }: { count: number; color: string; total: number }) {
	if (count === 0) return null
	const pct = total > 0 ? (count / total) * 100 : 0
	return <div style={{ width: `${pct}%`, backgroundColor: color, minWidth: '2px' }} aria-hidden='true' />
}

function QuestionDistributionBar({ distribution }: { distribution: ReportQuestion['distribution'] }) {
	// Bar denominator excludes Not Applicable — the bar represents the
	// distribution OF RATINGS. The NA count is rendered separately in the
	// per-bucket count row below.
	const ratedTotal =
		distribution.critical +
		distribution.needsImprovement +
		distribution.average +
		distribution.good +
		distribution.excellent
	if (ratedTotal === 0) return <div className='h-6 w-full rounded bg-neutral-100' />
	return (
		<div className='flex h-6 w-full overflow-hidden rounded' aria-hidden='true'>
			<DistributionSegment count={distribution.critical} color={DISTRIBUTION_TIERS[0].color} total={ratedTotal} />
			<DistributionSegment
				count={distribution.needsImprovement}
				color={DISTRIBUTION_TIERS[1].color}
				total={ratedTotal}
			/>
			<DistributionSegment count={distribution.average} color={DISTRIBUTION_TIERS[2].color} total={ratedTotal} />
			<DistributionSegment count={distribution.good} color={DISTRIBUTION_TIERS[3].color} total={ratedTotal} />
			<DistributionSegment count={distribution.excellent} color={DISTRIBUTION_TIERS[4].color} total={ratedTotal} />
		</div>
	)
}

type BucketCellSpec = {
	label: string
	count: number
	color: string
	textColor: string
}

function bucketCells(distribution: ReportQuestion['distribution']): BucketCellSpec[] {
	return [
		{
			label: 'Critical',
			count: distribution.critical,
			color: DISTRIBUTION_TIERS[0].color,
			textColor: DISTRIBUTION_TIERS[0].textColor,
		},
		{
			label: 'Needs Improvement',
			count: distribution.needsImprovement,
			color: DISTRIBUTION_TIERS[1].color,
			textColor: DISTRIBUTION_TIERS[1].textColor,
		},
		{
			label: 'Average',
			count: distribution.average,
			color: DISTRIBUTION_TIERS[2].color,
			textColor: DISTRIBUTION_TIERS[2].textColor,
		},
		{
			label: 'Good',
			count: distribution.good,
			color: DISTRIBUTION_TIERS[3].color,
			textColor: DISTRIBUTION_TIERS[3].textColor,
		},
		{
			label: 'Excellent',
			count: distribution.excellent,
			color: DISTRIBUTION_TIERS[4].color,
			textColor: DISTRIBUTION_TIERS[4].textColor,
		},
		{
			label: 'Not Applicable',
			count: distribution.notApplicable,
			color: NA_LEGEND_COLOR,
			textColor: NA_LEGEND_TEXT,
		},
	]
}

// 5 cells, one per rating bucket. Cell label is 1..5; the highlighted cell is
// the one matching the reviewer's bucketIndex (0-indexed → cell 1-5). Cell color
// = bucket color. Works regardless of underlying form scale.
const BUCKET_CELLS = [1, 2, 3, 4, 5] as const

// Highlighted cells use a *saturated* palette (deeper than the legend's soft
// pastels so the value stands out at small sizes); empty cells have a thin
// neutral border. Order matches DISTRIBUTION_TIERS: Critical → Excellent.
const BUCKET_CELL_HIGHLIGHT = [
	{ bg: '#DC2626', text: '#FFFFFF' }, // Critical
	{ bg: '#F87171', text: '#FFFFFF' }, // Needs Improvement
	{ bg: '#F59E0B', text: '#FFFFFF' }, // Average
	{ bg: '#3B82F6', text: '#FFFFFF' }, // Good
	{ bg: '#10B981', text: '#FFFFFF' }, // Excellent
]

const EMPTY_CELL_STYLE = {
	backgroundColor: '#FFFFFF',
	color: '#9CA3AF',
	border: '1px solid #E5E7EB',
}

function ReviewersGrid({ reviewers }: { reviewers: ReportReviewer[] }) {
	// Two-column grid (matches Figma); split reviewers down the middle.
	const half = Math.ceil(reviewers.length / 2)
	const columns = [reviewers.slice(0, half), reviewers.slice(half)]

	return (
		<div className='grid grid-cols-1 gap-x-8 gap-y-3 lg:grid-cols-2'>
			{columns.map((column, colIdx) => (
				<div key={`column-${colIdx}-${column[0]?.evaluatorId ?? 'empty'}`} className='flex flex-col gap-2'>
					<div className='flex items-center text-xs font-medium uppercase text-neutral-500 tracking-wider'>
						<div className='flex-1'>Reviewer Name</div>
						<div>Rating</div>
					</div>
					{column.map((r) => (
						<div key={r.evaluatorId} className='flex items-center gap-3'>
							<div className='flex-1 text-sm font-medium text-neutral-900 truncate'>{r.name}</div>
							<div className='flex items-center gap-1.5'>
								{BUCKET_CELLS.map((cell) => {
									const bucket = cell - 1
									const isMatch = r.bucketIndex === bucket
									const highlight = isMatch ? BUCKET_CELL_HIGHLIGHT[bucket] : null
									return (
										<div
											key={cell}
											className='flex h-11 w-11 items-center justify-center rounded-md text-base font-semibold'
											style={highlight ? { backgroundColor: highlight.bg, color: highlight.text } : EMPTY_CELL_STYLE}>
											{cell}
										</div>
									)
								})}
								<div
									className='flex h-11 min-w-[3rem] items-center justify-center rounded-md px-3 text-base font-semibold'
									style={
										r.rating === null ? { backgroundColor: NA_LEGEND_COLOR, color: NA_LEGEND_TEXT } : EMPTY_CELL_STYLE
									}>
									NA
								</div>
							</div>
						</div>
					))}
				</div>
			))}
		</div>
	)
}

function QuestionBlock({ question }: { question: ReportQuestion }) {
	const [showReviewers, setShowReviewers] = useState(false)
	const cells = bucketCells(question.distribution)
	const total = question.distribution.total
	const sources = SOURCE_ORDER.map((key) => ({
		key,
		label: SOURCE_LABELS[key],
		cell: question.sourceAggregates[key],
	}))

	return (
		<div className='flex flex-col gap-5 border-b border-neutral-100 pb-8 last:border-b-0 last:pb-0'>
			<p className='text-base font-semibold text-neutral-900'>{question.questionLabel}</p>

			<div className='flex flex-col gap-2'>
				<span className='text-xs font-medium text-neutral-500'>Rating Distribution</span>
				<QuestionDistributionBar distribution={question.distribution} />
			</div>

			<div className='grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3 lg:grid-cols-6'>
				{cells.map((c) => (
					<div key={c.label} className='flex flex-col gap-1'>
						<span className='text-xs text-neutral-500'>{c.label}</span>
						<span className='text-sm text-neutral-900'>
							<span className='font-semibold'>{c.count}</span>
							<span className='text-neutral-500'>
								/{total} ({total > 0 ? Math.round((c.count / total) * 100) : 0}%)
							</span>
						</span>
					</div>
				))}
			</div>

			<div className='grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3 lg:grid-cols-7'>
				{sources.map((s) => (
					<div key={s.key} className='flex flex-col gap-1'>
						<span className='text-xs text-neutral-500'>
							{s.label}
							{s.cell.count > 0 ? ` (${s.cell.count})` : ''}
						</span>
						<RatingChip value={s.cell.avg} />
					</div>
				))}
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Average Rating (exc. Self)</span>
					<RatingChip value={question.sourceAggregates.averageExcSelf} />
				</div>
			</div>

			<button
				type='button'
				onClick={() => setShowReviewers((v) => !v)}
				className='flex items-center gap-2 self-start text-sm font-medium text-blue-600 hover:underline'>
				{showReviewers ? 'Hide reviewers ratings' : 'Show reviewers ratings'}
				<span aria-hidden='true' className='text-xs'>
					{showReviewers ? '▲' : '▼'}
				</span>
			</button>

			{showReviewers && question.reviewers.length > 0 && <ReviewersGrid reviewers={question.reviewers} />}

			{showReviewers && question.reviewers.length === 0 && (
				<div className='py-6 text-center text-sm text-neutral-400'>No reviewer responses on this question.</div>
			)}
		</div>
	)
}

function ProfileSidebar({
	profile,
	participantName,
	onPrintPdf,
	onPrintEmployeePdf,
	isExportingPdf,
}: {
	profile: ParticipantProfile | null
	participantName: string
	onPrintPdf: () => void
	onPrintEmployeePdf: () => void
	isExportingPdf: boolean
}) {
	const fullName = profile
		? [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.email
		: participantName
	const managerName = profile?.manager
		? [profile.manager.firstName, profile.manager.lastName].filter(Boolean).join(' ') || '—'
		: '—'

	return (
		<aside className='flex flex-col gap-4 lg:w-72 lg:shrink-0 text-left'>
			<div className='rounded-lg border border-neutral-200 bg-neutral-50 p-5 flex flex-col gap-4'>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Employee name</span>
					<span className='text-base font-semibold text-neutral-900'>{fullName}</span>
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Title</span>
					<span className='text-sm font-medium text-neutral-800'>{profile?.jobTitle ?? '—'}</span>
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Date of hire</span>
					<span className='text-sm font-medium text-neutral-800'>{formatHireDate(profile?.hireDate ?? null)}</span>
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Manager</span>
					<span className='text-sm font-medium text-neutral-800'>{managerName}</span>
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Deals Involved</span>
					<span className='text-sm font-medium text-neutral-800'>
						{formatDealsInvolved(profile?.cycles.length ?? 0)}
					</span>
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Completion Rate</span>
					{profile?.completionRatePercent !== null && profile?.completionRatePercent !== undefined ? (
						<span
							className={`inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold w-fit ${
								profile.completionRatePercent >= 90
									? 'bg-emerald-100 text-emerald-600'
									: profile.completionRatePercent >= 70
										? 'bg-blue-100 text-blue-600'
										: profile.completionRatePercent >= 50
											? 'bg-orange-100 text-orange-600'
											: 'bg-red-100 text-red-600'
							}`}>
							{profile.completionRatePercent}%
						</span>
					) : (
						<span className='text-sm text-neutral-400'>—</span>
					)}
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Average Rating</span>
					<div>
						<RatingChip value={profile?.avgRating ?? null} />
					</div>
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Average vs. Overall</span>
					<VsOverall value={profile?.avgVsOverall ?? null} />
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Highest Rated Deal</span>
					{profile?.highestRatedDeal ? (
						<div className='flex items-center gap-2'>
							<RatingChip value={profile.highestRatedDeal.score} />
							<span className='text-sm font-semibold text-neutral-900 truncate'>{profile.highestRatedDeal.name}</span>
						</div>
					) : (
						<span className='text-sm text-neutral-400'>—</span>
					)}
				</div>
				<div className='flex flex-col gap-1'>
					<span className='text-xs text-neutral-500'>Lowest Rated Deal</span>
					{profile?.lowestRatedDeal ? (
						<div className='flex items-center gap-2'>
							<RatingChip value={profile.lowestRatedDeal.score} />
							<span className='text-sm font-semibold text-neutral-900 truncate'>{profile.lowestRatedDeal.name}</span>
						</div>
					) : (
						<span className='text-sm text-neutral-400'>—</span>
					)}
				</div>
			</div>

			<div className='rounded-lg border border-neutral-200 bg-white p-5 flex flex-col gap-3'>
				<span className='text-xs text-neutral-500'>Print</span>
				<button
					type='button'
					onClick={onPrintPdf}
					disabled={isExportingPdf || !profile}
					className='flex items-center justify-between rounded-md border border-neutral-200 px-3 py-2 text-left text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:text-neutral-400 disabled:hover:bg-transparent'>
					<span>{isExportingPdf ? 'Generating PDF…' : 'Print PDF'}</span>
					<span aria-hidden='true'>›</span>
				</button>
				<button
					type='button'
					onClick={onPrintEmployeePdf}
					disabled={isExportingPdf || !profile}
					className='flex items-center justify-between rounded-md border border-neutral-200 px-3 py-2 text-left text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:text-neutral-400 disabled:hover:bg-transparent'>
					<span>{isExportingPdf ? 'Generating PDF…' : 'Print employee view'}</span>
					<span aria-hidden='true'>›</span>
				</button>
			</div>
		</aside>
	)
}

export function ParticipantDealReportPage() {
	const { cycleId, userId } = useParams<{ cycleId: string; userId: string }>()
	const navigate = useNavigate()
	const organizationId = sessionStore((s) => s.user?.organizationId)
	const { isAuthorized, isLoading: accessLoading } = useDealTeamAccess()

	const {
		participantProfile,
		participantDealReport,
		isParticipantDealReportLoading,
		isParticipantProfileLoading,
		isExportingParticipantDealReportPdf,
		initialize,
		loadParticipantProfile,
		loadParticipantDealReport,
		clearParticipantDealReport,
		exportParticipantDealReportPdf,
		clearSelectedParticipant,
		isLoading,
		error,
		errorType,
	} = useAggregationStore()

	useEffect(() => {
		if (organizationId) initialize(organizationId)
	}, [organizationId, initialize])

	useEffect(() => {
		if (userId) loadParticipantProfile(userId)
		return () => clearSelectedParticipant()
	}, [userId, loadParticipantProfile, clearSelectedParticipant])

	useEffect(() => {
		if (cycleId && userId) loadParticipantDealReport(cycleId, userId)
		return () => clearParticipantDealReport()
	}, [cycleId, userId, loadParticipantDealReport, clearParticipantDealReport])

	if (accessLoading || isLoading) return <SpinnerWithBackground />

	if (!isAuthorized) {
		return (
			<main className='flex flex-col items-center justify-center p-8 gap-4'>
				<Text variant='large' weight='semibold'>
					Access Denied
				</Text>
				<Button variant='secondary' onClick={() => navigate('/dashboard')}>
					Back to Dashboard
				</Button>
			</main>
		)
	}

	if (error) {
		if (errorType === 'forbidden') {
			return (
				<main className='flex flex-col items-center justify-center p-8 gap-4'>
					<Text variant='large' weight='semibold'>
						Access Denied
					</Text>
					<Button variant='secondary' onClick={() => navigate('/dashboard')}>
						Back to Dashboard
					</Button>
				</main>
			)
		}
		return (
			<main className='flex flex-col items-center justify-center p-8 gap-4'>
				<Text variant='large' weight='semibold' className='text-red-600'>
					Something went wrong
				</Text>
				<Text variant='small' className='text-neutral-500'>
					{error}
				</Text>
			</main>
		)
	}

	const dealName = participantDealReport?.dealName ?? 'Deal'
	const participantName = participantDealReport?.participantName ?? 'Participant'

	return (
		<main className='flex flex-col gap-6 p-6 lg:flex-row lg:items-start text-left'>
			<ProfileSidebar
				profile={participantProfile}
				participantName={participantName}
				onPrintPdf={() => {
					if (cycleId && userId) exportParticipantDealReportPdf(cycleId, userId, 'admin')
				}}
				onPrintEmployeePdf={() => {
					if (cycleId && userId) exportParticipantDealReportPdf(cycleId, userId, 'employee')
				}}
				isExportingPdf={isExportingParticipantDealReportPdf}
			/>

			<section className='flex-1 flex flex-col gap-6 min-w-0'>
				<div className='flex flex-col gap-2'>
					<button
						type='button'
						className='self-start text-sm text-neutral-500 hover:text-neutral-700'
						onClick={() => navigate(`/dealteam/deals/${cycleId}`)}>
						← Back to Deal
					</button>
					<Text variant='hint' className='text-neutral-500'>
						Dashboard &gt; DealTeam Feedback &gt; {dealName} &gt; {participantName}
					</Text>
					<Text variant='h3' weight='semibold'>
						DealTeam Feedback Report
					</Text>
				</div>

				{(isParticipantDealReportLoading || isParticipantProfileLoading) && <SpinnerWithBackground />}

				{!isParticipantDealReportLoading && !participantDealReport && (
					<div className='py-12 text-center text-neutral-500'>
						<Text variant='small'>No report data available.</Text>
					</div>
				)}

				{!isParticipantDealReportLoading && participantDealReport && (
					<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-8'>
						<ColorLegend />
						{participantDealReport.questions.length === 0 && (
							<div className='py-12 text-center text-neutral-500'>
								<Text variant='small'>No rating responses recorded for this participant on this deal.</Text>
							</div>
						)}
						{participantDealReport.questions.map((q) => (
							<QuestionBlock key={q.questionId} question={q} />
						))}
					</div>
				)}
			</section>
		</main>
	)
}
